import { Fragment } from "preact";
import { useContext } from "preact/hooks";
import { Text } from "preact-i18n";

import { Configs } from "../../helpers/context";
import { formatIntegerCurrencyString } from "../../helpers/formatters";
import { formatDate } from "../../helpers/formatters/date";
import { FundingSourceType } from "../../enums/fundingSourceType";

import { FeeProgramType } from "../../enums/feeProgramType";
import { capitalizeFirst } from "../../helpers/common";

const OrderSummaryAmounts = ({
  subTotal,
  discountTotal,
  feeTotal,
  taxTotal,
  shippingTotal,
  tipAmount,
  total,
  payLink,
}) => {
  const configsContext = useContext(Configs);
  const hasAdjustments = discountTotal || feeTotal || taxTotal || shippingTotal || tipAmount;
  const havePaidPaymentRequests = (payLink?.invoice?.paymentRequests || []).some(
    (paymentRequest) =>
      paymentRequest.status === "CAPTURED" || paymentRequest.status === "AUTHORIZED",
  );
  const getFundingSourceLabel = (fundingSource) => {
    switch (fundingSource?.type) {
      case FundingSourceType.CREDIT_DEBIT:
        return fundingSource?.card
          ? `${capitalizeFirst(fundingSource?.card?.type ?? "")} ****  ${
              fundingSource?.card?.numberLast4
            }`
          : "Card";
      case FundingSourceType.CHEQUE:
        return `${fundingSource.bankAccount?.bankName || "Bank"} ***** ${
          fundingSource?.bankAccount?.accountNumberLastFour || ""
        }`;
      case FundingSourceType.CASH:
        return "Cash";
      case FundingSourceType.CUSTOM_FUNDING_SOURCE:
        return fundingSource?.customFundingSource?.type === "CHEQUE" ? "Check" : "Other";
      default:
        return "Other";
    }
  };
  const getSurchargeAmount = (transaction) => {
    return (
      transaction?.amounts?.transactionFees?.find(
        (fee) => fee.feeProgramType === FeeProgramType.SURCHARGE,
      )?.amount || 500
    );
  };
  return (
    <Fragment>
      {subTotal !== total ? (
        <Fragment>
          <div className="order-subtotal">
            <span style="float:left;">
              <Text id="CHECKOUT_SUBTOTAL">Subtotal</Text>
            </span>
            <span style="float:right;">
              {formatIntegerCurrencyString(
                subTotal,
                configsContext?.configs?.currency,
                configsContext?.configs?.ecommerceLocaleLanguage,
              )}
            </span>
            <div style="clear:both;"></div>
          </div>
          {hasAdjustments ? (
            <div>
              <div class="order-summary-divider"></div>
              <div className="order-adjustments-header">
                <span>
                  <Text id="CHECKOUT_ADJUSTMENTS">Adjustments</Text>
                </span>
              </div>
            </div>
          ) : null}
          {discountTotal ? (
            <div className="order-tax">
              <span style="float:left;">
                <Text id="CHECKOUT_DISCOUNT">Discount</Text>
              </span>
              <span style="float:right;">
                {formatIntegerCurrencyString(
                  discountTotal > 0 ? (-1 * discountTotal) : discountTotal,
                  configsContext?.configs?.currency,
                  configsContext?.configs?.ecommerceLocaleLanguage,
                )}
              </span>
              <div style="clear:both;"></div>
            </div>
          ) : null}
          {feeTotal ? (
            <div className="order-tax">
              <span style="float:left;">
                <Text id="CHECKOUT_FEES">Fees</Text>
              </span>
              <span style="float:right;">
                {formatIntegerCurrencyString(
                  feeTotal,
                  configsContext?.configs?.currency,
                  configsContext?.configs?.ecommerceLocaleLanguage,
                )}
              </span>
              <div style="clear:both;"></div>
            </div>
          ) : null}
          {taxTotal ? (
            <div className="order-tax">
              <span style="float:left;">
                <Text id="CHECKOUT_TAX">Tax</Text>
              </span>
              <span style="float:right;">
                {formatIntegerCurrencyString(
                  taxTotal,
                  configsContext?.configs?.currency,
                  configsContext?.configs?.ecommerceLocaleLanguage,
                )}
              </span>
              <div style="clear:both;"></div>
            </div>
          ) : null}
          {shippingTotal ? (
            <div className="order-tax">
              <span style="float:left;">
                <Text id="CHECKOUT_SHIPPING">Shipping</Text>
              </span>
              <span style="float:right;">
                {formatIntegerCurrencyString(
                  shippingTotal,
                  configsContext?.configs?.currency,
                  configsContext?.configs?.ecommerceLocaleLanguage,
                )}
              </span>
              <div style="clear:both;"></div>
            </div>
          ) : null}
          {tipAmount ? (
            <div className="order-tax">
              <span style="float:left;">
                <Text id="CHECKOUT_TIP">Tip</Text>
              </span>
              <span style="float:right;">
                {formatIntegerCurrencyString(
                  tipAmount,
                  configsContext?.configs?.currency,
                  configsContext?.configs?.ecommerceLocaleLanguage,
                )}
              </span>
              <div style="clear:both;"></div>
            </div>
          ) : null}
          <div class="order-summary-divider"></div>
        </Fragment>
      ) : null}
      {havePaidPaymentRequests ? (
        <Fragment>
          <div className="invoice-payment-request-total">
            <div className="invoice-payment-request-total-amount">
              <span style="float:left">
                <Text id="CHECKOUT_INVOICE_TOTAL">Invoice Total</Text>
              </span>
              <span style="float:right">
                {formatIntegerCurrencyString(
                  total,
                  configsContext?.configs?.currency,
                  configsContext?.configs?.ecommerceLocaleLanguage,
                )}
              </span>
              <div style="clear:both;"></div>
            </div>
            {(payLink.transactions || []).map((txn) => (
              <div className="order-amounts">
                <span style="float:left">
                  <Text id="CHECKOUT_PAYMENT">Payment</Text>
                  <span className="order-amounts-ml-xs">
                    ({getFundingSourceLabel(txn.fundingSource)})
                  </span>
                  <span className="order-amounts-sub-text">
                    {formatDate(txn.createdAt, configsContext?.configs?.ecommerceLocaleLanguage, {
                      year: "numeric",
                      month: "short",
                      day: "2-digit",
                    })}
                  </span>
                </span>
                <span style="float:right;text-align:right">
                  {formatIntegerCurrencyString(
                    txn.amounts.transactionAmount,
                    configsContext?.configs?.currency,
                    configsContext?.configs?.ecommerceLocaleLanguage,
                  )}
                  {getSurchargeAmount(txn) > 0 && (
                    <div className="order-amounts-bottom-sub-text">
                      +
                      {formatIntegerCurrencyString(
                        getSurchargeAmount(txn),
                        configsContext?.configs?.currency,
                        configsContext?.configs?.ecommerceLocaleLanguage,
                      )}{" "}
                      <Text id="CHECKOUT_SURCHARGE_FEE">Surcharge Fee</Text>
                    </div>
                  )}
                </span>
                <div style="clear:both;"></div>
              </div>
            ))}
          </div>
          <div className="order-summary-divider"></div>
          <div className="order-total">
            <span style="float:left">
              <Text id="CHECKOUT_TOTAL_DUE">Total Due</Text>
            </span>
            <span style="float:right;">
              {formatIntegerCurrencyString(
                payLink.remainingAmount,
                configsContext?.configs?.currency,
                configsContext?.configs?.ecommerceLocaleLanguage,
              )}
            </span>
            <div style="clear:both;"></div>
          </div>
        </Fragment>
      ) : (
        <div className="order-total">
          <span style="float:left">
            <Text id="CHECKOUT_TOTAL">Total</Text>
          </span>
          <span style="float:right;">
            {formatIntegerCurrencyString(
              total,
              configsContext?.configs?.currency,
              configsContext?.configs?.ecommerceLocaleLanguage,
            )}
          </span>
          <div style="clear:both;"></div>
        </div>
      )}
    </Fragment>
  );
};

export default OrderSummaryAmounts;
